import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import useUploadedRecords from './useUploadedRecords';
import {
	convertPendingShareToUploadedRecord,
	filterByClassificationTags,
	filterByRecordTypes,
	filterBySearchQuery,
	filterBySpecialFilters,
	getRecordPrivacy,
	matchesClassificationTags,
	matchesSpecialFilters,
	PendingShareStatuses,
	PrivacyFilters,
	type PrivacyFilterType,
	RecordTypeFilter,
} from '@/utils/uploadItemUtils';
import { listReceivedPendingSharesHousesHouseIdDocumentsPendingReceivedGetOptions } from '@/client/@tanstack/react-query.gen';

export { PrivacyFilters, type PrivacyFilterType } from '@/utils/uploadItemUtils';

interface HouseRecordsOptions {
	refetchInterval?: number | false;
}

/**
 * Hook for fetching, searching, and filtering house records
 * Encapsulates unified uploads fetching, search, and filtering functionality
 * Also fetches received pending shares for looking up share status
 * Provides materializedDocument lookup to find documents by source_document_id
 * Supports multi-select classification tag filtering and special filters (important, pending)
 */
const useHouseRecords = (houseId: number, options: HouseRecordsOptions = {}) => {
	const [searchQuery, setSearchQuery] = useState('');
	const [privacyFilter, setPrivacyFilter] = useState<PrivacyFilterType>(PrivacyFilters.All);
	const [selectedClassificationTags, setSelectedClassificationTags] = useState<string[]>([]);
	const [selectedSpecialFilters, setSelectedSpecialFilters] = useState<string[]>([]);
	const [selectedRecordTypes, setSelectedRecordTypes] = useState<RecordTypeFilter[]>([]);

	// Use the useUploadedRecords hook to fetch documents-only list for UI
	// TODO(records-realtime): Replace this polling fallback with a realtime list-invalidation event
	// when another user adds, removes, or changes the visibility of a record.
	const { uploadItems, isLoading, refetch, activeUpdateDocIds } = useUploadedRecords(houseId, {
		refetchInterval: options.refetchInterval,
	});

	// Fetch received pending shares to look up share status by pending_share_id
	const { data: receivedPendingDocuments = [], isLoading: isReceivedSharesLoading } = useQuery({
		...listReceivedPendingSharesHousesHouseIdDocumentsPendingReceivedGetOptions({
			path: { house_id: houseId },
		}),
		staleTime: 1000 * 60 * 1, // 1 minute
	});

	/**
	 * Finds a materialized document in the current house by source_document_id
	 * Used when navigating from notifications to find the corresponding document in the current house
	 */
	const findMaterializedDocument = useMemo(
		() => (sourceDocumentId: number) => {
			return uploadItems.find((item) => item.document.source_document_id === sourceDocumentId)?.document;
		},
		[uploadItems],
	);

	// Convert pending shares to UploadedRecord format
	const pendingRecords = useMemo(
		() =>
			receivedPendingDocuments
				.filter((share) => share.status === PendingShareStatuses.Pending)
				.map(convertPendingShareToUploadedRecord),
		[receivedPendingDocuments],
	);

	// Merge pending shares with regular upload items
	// Sort to ensure pending documents appear at the top while preserving API order for regular documents
	const mergedItems = useMemo(() => {
		const items = [...pendingRecords, ...uploadItems];

		// Newest-first. Decorate with a parsed timestamp so each created_at is parsed once rather
		// than O(n log n) times inside the comparator, and so the sort maps to a fresh array — it
		// never mutates the shared uploadItems/pendingRecords array in place. Pending-share records
		// are floated to the top downstream by orderPendingRecordsFirst (table + mobile list).
		return items
			.map((item) => ({
				item,
				createdAt: item.document.created_at ? new Date(item.document.created_at).getTime() : 0,
			}))
			.sort((a, b) => b.createdAt - a.createdAt)
			.map((entry) => entry.item);
	}, [uploadItems, pendingRecords]);

	// Filter merged items by type, classification tags, special filters, and search query. Keep this
	// privacy-neutral list stable when only the privacy lens changes: the records UI retains those
	// rows in the DOM so returning to "All" does not have to mount them again.
	const privacyFilterCandidates = useMemo(() => {
		// First filter by document type
		const filteredByDocType = filterByRecordTypes(mergedItems, selectedRecordTypes);

		// If both classification tags and special filters are selected, combine with OR logic
		// Otherwise, apply each filter independently
		let filtered: typeof mergedItems;

		if (selectedClassificationTags.length > 0 && selectedSpecialFilters.length > 0) {
			// OR logic: show items that match ANY classification tag OR ANY special filter
			filtered = filteredByDocType.filter(
				(item) =>
					matchesClassificationTags(item, selectedClassificationTags) ||
					matchesSpecialFilters(item, selectedSpecialFilters),
			);
		} else if (selectedClassificationTags.length > 0) {
			// Only classification tags selected
			filtered = filterByClassificationTags(filteredByDocType, selectedClassificationTags);
		} else if (selectedSpecialFilters.length > 0) {
			// Only special filters selected
			filtered = filterBySpecialFilters(filteredByDocType, selectedSpecialFilters);
		} else {
			// No filters selected
			filtered = filteredByDocType;
		}

		return filterBySearchQuery(filtered, searchQuery);
	}, [mergedItems, selectedRecordTypes, selectedClassificationTags, selectedSpecialFilters, searchQuery]);

	const filteredUploadItems = useMemo(() => {
		if (privacyFilter === PrivacyFilters.All) return privacyFilterCandidates;
		return privacyFilterCandidates.filter((item) => getRecordPrivacy(item) === privacyFilter);
	}, [privacyFilter, privacyFilterCandidates]);

	return {
		uploadItems,
		allRecordItems: mergedItems,
		filteredUploadItems,
		privacyFilterCandidates,
		searchQuery,
		setSearchQuery,
		privacyFilter,
		setPrivacyFilter,
		selectedClassificationTags,
		setSelectedClassificationTags,
		selectedSpecialFilters,
		setSelectedSpecialFilters,
		selectedRecordTypes,
		setSelectedRecordTypes,
		isLoading,
		isReceivedSharesLoading,
		refetch,
		receivedPendingDocuments,
		findMaterializedDocument,
		activeUpdateDocIds,
	};
};

export default useHouseRecords;
